import React, { useState, useEffect, useCallback } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { format } from 'date-fns';
import styled from 'styled-components';
import API_URL from '../config';
import {
  Card,
  FilterRow,
  FieldGroup,
  FieldLabel,
  DateInputWrap,
  FetchBtn,
  Divider,
  StatusText,
  ErrorText,
  StatsGrid,
  MetricCard,
  MetricLabel,
  MetricValue,
  MetricUnit,
  SectionTitle,
} from './statsStyles';

/**
 * Прослушивания в мобильном приложении «Голос Надежды».
 *
 * Отдельная страница, а не раздел в «Статистике»: там счётчики сайта по
 * папкам аудио, и складывать их с приложением нельзя — это разные аудитории.
 */

// ─── Styled ───────────────────────────────────────────────────────────────────

const PlatformRow = styled.div`
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;
`;

const PlatformCard = styled.div`
  flex: 1 1 220px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 1rem 1.1rem;
`;

const PlatformName = styled.p`
  margin: 0 0 0.6rem 0;
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
`;

const PlatformLine = styled.p`
  margin: 0;
  font-size: 13px;
  color: #475569;
  display: flex;
  justify-content: space-between;
  padding: 0.2rem 0;

  span:last-child { font-weight: 600; color: #0f172a; }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;

  th {
    text-align: left;
    font-size: 12px;
    font-weight: 600;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 0.5rem 0.75rem;
    border-bottom: 1px solid #e2e8f0;
  }

  td {
    padding: 0.55rem 0.75rem;
    border-bottom: 1px solid #f1f5f9;
    color: #334155;
  }

  td.num, th.num { text-align: right; font-variant-numeric: tabular-nums; }
  tr:last-child td { border-bottom: none; }
`;

const ScrollBox = styled.div`
  max-height: 320px;
  overflow-y: auto;
`;

const ItemTitle = styled.span`
  color: #0f172a;
`;

const ItemSub = styled.span`
  display: block;
  font-size: 12px;
  color: #94a3b8;
`;

const Columns = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 1.75rem;
`;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const KINDS = [
  { key: 'all',     label: 'Всего' },
  { key: 'radio',   label: 'Радио' },
  { key: 'podcast', label: 'Подкасты' },
  { key: 'bible',   label: 'Библия' },
];

const PLATFORMS = [
  { key: 'ios',     label: 'iPhone / iPad' },
  { key: 'android', label: 'Android' },
];

const num = (value) => (value ?? 0).toLocaleString('ru-RU');

const prettyDate = (isoDate) => {
  const [year, month, day] = isoDate.split('-');
  return `${day}.${month}.${year}`;
};

function TopTable({ title, items, emptyText }) {
  return (
    <div>
      <SectionTitle>{title}</SectionTitle>
      {items.length === 0 ? (
        <StatusText>{emptyText}</StatusText>
      ) : (
        <ScrollBox>
          <Table>
            <thead>
              <tr>
                <th>Название</th>
                <th className="num">Прослушиваний</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.itemId}>
                  <td>
                    <ItemTitle>{item.title || item.itemId}</ItemTitle>
                    {item.subtitle && <ItemSub>{item.subtitle}</ItemSub>}
                  </td>
                  <td className="num">{num(item.plays)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </ScrollBox>
      )}
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

const GolosnStats = () => {
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d;
  });
  const [endDate, setEndDate] = useState(new Date());
  const [summary, setSummary] = useState(null);
  const [topPodcasts, setTopPodcasts] = useState([]);
  const [topBible, setTopBible] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError('');
    const from = format(startDate, 'yyyy-MM-dd');
    const to   = format(endDate,   'yyyy-MM-dd');
    const range = `from=${from}&to=${to}`;

    try {
      const responses = await Promise.all([
        fetch(`${API_URL}/api/app-stats/summary?${range}`),
        fetch(`${API_URL}/api/app-stats/top?kind=podcast&${range}&limit=20`),
        fetch(`${API_URL}/api/app-stats/top?kind=bible&${range}&limit=20`),
      ]);
      if (responses.some((response) => !response.ok)) {
        throw new Error('Ошибка при получении данных');
      }
      const [summaryData, podcastData, bibleData] = await Promise.all(
        responses.map((response) => response.json()),
      );
      setSummary(summaryData);
      setTopPodcasts(podcastData.items);
      setTopBible(bibleData.items);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const handleSubmit = (e) => { e.preventDefault(); fetchStats(); };

  const hasPlays = summary && summary.total.all > 0;

  return (
    <Card>
      <form onSubmit={handleSubmit}>
        <FilterRow>
          <FieldGroup>
            <FieldLabel>Начало периода</FieldLabel>
            <DateInputWrap>
              <DatePicker
                selected={startDate}
                onChange={setStartDate}
                dateFormat="dd.MM.yyyy"
                required
              />
            </DateInputWrap>
          </FieldGroup>

          <FieldGroup>
            <FieldLabel>Конец периода</FieldLabel>
            <DateInputWrap>
              <DatePicker
                selected={endDate}
                onChange={setEndDate}
                dateFormat="dd.MM.yyyy"
                required
              />
            </DateInputWrap>
          </FieldGroup>

          <FetchBtn type="submit" disabled={loading}>
            {loading ? (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{animation:'spin 1s linear infinite'}}>
                  <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                </svg>
                Загрузка…
              </>
            ) : 'Обновить'}
          </FetchBtn>
        </FilterRow>
      </form>

      {error && <ErrorText>⚠ {error}</ErrorText>}

      {!loading && !error && hasPlays && (
        <>
          <Divider />
          <SectionTitle>Прослушиваний за период</SectionTitle>
          <StatsGrid>
            {KINDS.map(({ key, label }) => (
              <MetricCard key={key}>
                <MetricLabel>{label}</MetricLabel>
                <MetricValue>
                  {num(summary.total[key])}
                  <MetricUnit>раз</MetricUnit>
                </MetricValue>
              </MetricCard>
            ))}
          </StatsGrid>

          <Divider />
          <SectionTitle>По платформам</SectionTitle>
          <PlatformRow>
            {PLATFORMS.map(({ key, label }) => (
              <PlatformCard key={key}>
                <PlatformName>{label}</PlatformName>
                {KINDS.map(({ key: kind, label: kindLabel }) => (
                  <PlatformLine key={kind}>
                    <span>{kindLabel}</span>
                    <span>{num(summary.byPlatform[key]?.[kind])}</span>
                  </PlatformLine>
                ))}
              </PlatformCard>
            ))}
          </PlatformRow>

          <Divider />
          <Columns>
            <TopTable
              title="Самые слушаемые выпуски"
              items={topPodcasts}
              emptyText="Выпуски за период не слушали"
            />
            <TopTable
              title="Самые слушаемые главы Библии"
              items={topBible}
              emptyText="Главы за период не слушали"
            />
          </Columns>

          <Divider />
          <SectionTitle>По дням</SectionTitle>
          <ScrollBox>
            <Table>
              <thead>
                <tr>
                  <th>Дата</th>
                  <th className="num">Радио</th>
                  <th className="num">Подкасты</th>
                  <th className="num">Библия</th>
                  <th className="num">Всего</th>
                </tr>
              </thead>
              <tbody>
                {summary.byDay.map((day) => (
                  <tr key={day.date}>
                    <td>{prettyDate(day.date)}</td>
                    <td className="num">{num(day.radio)}</td>
                    <td className="num">{num(day.podcast)}</td>
                    <td className="num">{num(day.bible)}</td>
                    <td className="num">{num(day.all)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </ScrollBox>
        </>
      )}

      {!loading && !error && !hasPlays && (
        <StatusText>Нет данных за выбранный период</StatusText>
      )}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </Card>
  );
};

export default GolosnStats;
